import { useMocks } from '../../shared/env';
import { createJsonClient } from '../../shared/httpClient';
import type { EmployeeInvitationSettings } from './types';

export interface EmployeeInvitationSettingsGateway {
  get(signal?: AbortSignal): Promise<EmployeeInvitationSettings>;
  /** `null` clears the company's own value (back to the platform default). */
  save(lifetimeHours: number | null): Promise<EmployeeInvitationSettings>;
}

class HttpEmployeeInvitationSettingsGateway implements EmployeeInvitationSettingsGateway {
  // Owned by Identity (same origin, no service prefix), like the invitations themselves.
  private readonly request = createJsonClient('');

  get(signal?: AbortSignal): Promise<EmployeeInvitationSettings> {
    return this.request('/api/employee-invitations/settings', undefined, signal);
  }

  save(lifetimeHours: number | null): Promise<EmployeeInvitationSettings> {
    return this.request('/api/employee-invitations/settings', { method: 'PUT', body: JSON.stringify({ lifetimeHours }) });
  }
}

/** Local-only stand-in (VITE_USE_MOCKS): the same default, ceiling and bounds the server enforces, kept in memory. */
export class MockEmployeeInvitationSettingsGateway implements EmployeeInvitationSettingsGateway {
  private company: number | null = null;
  private readonly defaultHours = 24;
  private readonly maxHours = 720;

  private view(): EmployeeInvitationSettings {
    return {
      lifetimeHours: Math.min(this.company ?? this.defaultHours, this.maxHours),
      companyLifetimeHours: this.company,
      defaultLifetimeHours: this.defaultHours,
      maxLifetimeHours: this.maxHours,
    };
  }

  async get(): Promise<EmployeeInvitationSettings> {
    return this.view();
  }

  async save(lifetimeHours: number | null): Promise<EmployeeInvitationSettings> {
    if (lifetimeHours !== null && (!Number.isInteger(lifetimeHours) || lifetimeHours < 1 || lifetimeHours > this.maxHours)) {
      throw new Error(`The invitation link lifetime must be between 1 and ${this.maxHours} hours.`);
    }
    this.company = lifetimeHours;
    return this.view();
  }
}

export const employeeInvitationSettingsGateway: EmployeeInvitationSettingsGateway = useMocks
  ? new MockEmployeeInvitationSettingsGateway()
  : new HttpEmployeeInvitationSettingsGateway();
